/**
 * Your own admin account (D77): role, session, and changing your password. With a temporary
 * password (new employee or after a reset) the panel shows only the forced version of this form.
 */
import {
  Badge,
  Button,
  Card,
  CardTitle,
  CodeInput,
  Field,
  Input,
  Notice,
  Row,
  formatDateTime,
} from "@custos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { PageHeader, useLogout } from "../components/AdminShell";
import { AuthLayout } from "../components/AuthLayout";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { qk, useAdminMe } from "../lib/queries";

const MIN = 12;

function ChangePasswordForm({ temporary, onDone }: { temporary: boolean; onDone: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [code, setCode] = useState("");
  const change = useMutation({
    mutationFn: () => api.changeAdminPassword(current, next, temporary ? undefined : code),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setRepeat("");
      setCode("");
      onDone();
    },
  });
  const tooShort = next.length > 0 && next.length < MIN;
  const mismatch = repeat.length > 0 && repeat !== next;
  const ok =
    current.length > 0 &&
    next.length >= MIN &&
    next === repeat &&
    next !== current &&
    (temporary || code.length === 6);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) change.mutate();
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <Field label={temporary ? "Temporary password" : "Current password"}>
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        )}
      </Field>
      <Field
        label="New password"
        hint={`At least ${MIN} characters. Only you should know it.`}
        error={tooShort ? `At least ${MIN} characters` : null}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        )}
      </Field>
      <Field label="Repeat new password" error={mismatch ? "The passwords don't match" : null}>
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        )}
      </Field>
      {!temporary && (
        <Field label="Your 2FA code">{(p) => <CodeInput {...p} value={code} onChange={setCode} />}</Field>
      )}
      {change.error && <Notice tone="danger">{errorMessage(change.error)}</Notice>}
      {change.isSuccess && !temporary && (
        <Notice tone="success">Password changed. Your other sessions were logged out.</Notice>
      )}
      <Button type="submit" block loading={change.isPending} disabled={!ok}>
        {temporary ? "Save my new password" : "Change password"}
      </Button>
    </form>
  );
}

/** Shown instead of the panel while the admin still has a temporary password. */
export function ForcedPasswordPage() {
  const queryClient = useQueryClient();
  const logout = useLogout();
  return (
    <AuthLayout
      title="Choose your password"
      subtitle="You signed in with a temporary password. Pick your own before using the admin panel."
    >
      <ChangePasswordForm temporary onDone={() => void queryClient.invalidateQueries({ queryKey: qk.me })} />
      <button
        type="button"
        onClick={logout}
        className="mt-4 min-h-11 w-full text-sm text-muted hover:text-fg"
      >
        Log out
      </button>
    </AuthLayout>
  );
}

export function AccountPage() {
  const me = useAdminMe();
  const m = me.data;
  return (
    <>
      <PageHeader title="My account" subtitle={m?.email} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle>Role</CardTitle>
            {m && <Badge tone={m.role.isOwner ? "primary" : "neutral"}>{m.role.name}</Badge>}
          </div>
          {m && (
            <div>
              <Row label="Session ends">{formatDateTime(m.sessionExpiresAt)}</Row>
              <Row label="IP allowlist">{m.ipAllowlist.length ? m.ipAllowlist.join(", ") : "any IP"}</Row>
              <Row label="Permissions">{m.role.isOwner ? "all" : String(m.permissions.length)}</Row>
            </div>
          )}
        </Card>
        <DisplayNameCard />
        <Card className="flex flex-col gap-3">
          <CardTitle>Change password</CardTitle>
          <ChangePasswordForm temporary={false} onDone={() => undefined} />
        </Card>
      </div>
    </>
  );
}

/** The name customers see on your support chat replies (D79). Empty = "Aurix Support". */
function DisplayNameCard() {
  const me = useAdminMe();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const valid = name.trim().length <= 30 && /^[p{L}p{M} .'-]*$/u.test(name.trim());
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>Name on support replies</CardTitle>
      <p className="text-sm text-muted">
        Customers see <b className="text-fg">{me.data?.displayName ?? "Aurix Support"}</b> on your chat
        replies.
      </p>
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => {
          setName(me.data?.displayName ?? "");
          setOpen(true);
        }}
      >
        Change name
      </Button>
      <StepUpModal
        open={open}
        onOpenChange={setOpen}
        title="Name on support replies"
        description="A first name or nickname. Leave empty to show Aurix Support."
        confirmLabel="Save name"
        canConfirm={valid}
        onConfirm={(code) => api.setDisplayName(name.trim(), code)}
        onDone={() => void queryClient.invalidateQueries({ queryKey: qk.me })}
      >
        <Field
          label="Display name"
          error={valid ? null : "Up to 30 letters, spaces, dots, apostrophes or hyphens"}
        >
          {(p) => <Input {...p} value={name} maxLength={30} onChange={(e) => setName(e.target.value)} />}
        </Field>
      </StepUpModal>
    </Card>
  );
}
